import { AnswerRecord, PracticeQuestion, QuestionType } from "@/constant/schema";
import MarkdownComponent from "../Markdown";
import MathField from "../Math/math-field";
import { isEquivalentMathExpression } from "@/lib/utils";
import { colors } from "@/constant/colors";
import PracticeDictation from "./practice-dictation";
import ExpandExplanation from "./expandExplanation";
import { HighlighterQuestion, HighlighterSelector } from "./highlighter-question";
import FillingQuestion from "./filling-question";
import DragAndDropQuestion from "./drag-and-drop-question";
import NormalButton from "../Button/normal";

interface ReviewAnswerProps {
  answerRecords: AnswerRecord[];
  allQuestions: PracticeQuestion[];
  isSolution?: boolean;
  onClose: () => void;
}

const titleStyle: React.CSSProperties = {
  textAlign: 'left',
  color: 'black',
  fontSize: '20px',
  fontWeight: '500',
  lineHeight: '2'
}

const questionStyle: React.CSSProperties = {
  textAlign: 'left',
  color: colors.mainTextColor,
  fontSize: '20px',
  fontWeight: '400',
}

const ReviewGrammarDragAndDrop = ({ question, answer, questionNumber }: { question: PracticeQuestion, answer: AnswerRecord | null, questionNumber: number }) => {
  return (
    <div className="w-full text-[#573F35]">
      <div className="text-xl text-[#573F35] pb-4 flex items-center gap-3">
        <span style={{ ...titleStyle }}>{questionNumber}. </span>
        <MarkdownComponent content="Choose the Best Word" textStyle={titleStyle} />
      </div>
      <DragAndDropQuestion question={question} handleAnswer={() => { }} currentAnswer={answer} disabled={true} isReview={true} submitted={true} />
    </div>
  )
}

const ReviewDictation = ({ question, answer, questionNumber }: { question: PracticeQuestion, answer: AnswerRecord | null, questionNumber: number }) => {
  return (
    <div className="w-full text-[#573F35]">
      <div className="text-xl text-[#573F35] pb-4 flex items-center gap-3">
        <span style={{ ...titleStyle }}>{questionNumber}.</span>
        <MarkdownComponent content="Listen carefully, and write down the word you hear" textStyle={titleStyle} />
      </div>
      <PracticeDictation question={question} handleAnswer={() => { }} currentAnswer={answer} disabled={true} isReview={true} />
    </div>
  )
}

const ReviewHighlight = ({ question, answer, questionNumber }: { question: PracticeQuestion, answer: AnswerRecord | null, questionNumber: number }) => {
  // 确保 rightAnswer 一定是数组
  const correctAnswer = Array.isArray(question.answer) ? question.answer : JSON.parse(question.answer || "[]");
  return (
    <div className="w-full text-[#573F35] flex flex-col">
      <div className="text-xl text-[#573F35] pb-4 flex items-center gap-3">
        <span style={{ ...titleStyle }}>{questionNumber}.</span>
        <MarkdownComponent content="Highlight the words in the sentence with the corresponding colors." textStyle={titleStyle} />
      </div>
      <div className="w-full flex flex-col items-center justify-center">
        <HighlighterSelector className="items-center justify-center" answer={correctAnswer} selectedTag={""} onSelect={() => { }} isReview={true} />
      </div>
      <HighlighterQuestion className="w-full mt-2 text-center text-xl font-semibold" question={question.question} correctAnswer={correctAnswer} userAnswer={[]} disabled={true} selectedTag={""} mode={'truth'} onHighlightChange={() => { }} />
      {answer && <div className="flex flex-row mt-5">
        <span className="text-xl" style={{ color: colors.mainTextColor }}>Your Answer: </span>
        <span className="text-xl font-bold" style={{ color: answer.firstIsCorrect ? colors.correctAnswerColor : colors.wrongAnswerColor }}>{answer.firstIsCorrect ? "Correct" : "Wrong"}</span>
      </div>}
      {answer && !answer.firstIsCorrect && (
        <HighlighterQuestion className="w-full text-center text-xl font-semibold" question={question.question} correctAnswer={correctAnswer} userAnswer={JSON.parse(answer && answer.firstSelectedOption || "[]")} disabled={true} selectedTag={""} mode={'judge'} onHighlightChange={() => { }} />
      )}
      <div className="w-full flex flex-col justify-center items-center">
        <ExpandExplanation explanation={question?.explanation || ''} initialHideExplanation={answer && answer.firstIsCorrect || true} />
      </div>

    </div>
  )
}

const ReviewMathFilling = ({ question, answer, questionNumber }: { question: PracticeQuestion, answer: AnswerRecord | null, questionNumber: number }) => {
  return (
    <div className="w-full">
      <div className="text-xl text-[#573F35] pb-4 flex gap-3 items-start">
        <span style={{ ...titleStyle }}>{questionNumber}.</span>
        <MarkdownComponent content={question?.question} textStyle={titleStyle} />
      </div>
      <div className="flex flex-col w-full">
        <div className="w-full flex flex-col justify-center" style={{ color: colors.mainTextColor }}>
          {answer && <span className="text-[1.25rem]">Your Answer: <MathField latex={answer.firstIsCorrect === undefined ? answer.selectedOption : answer.firstSelectedOption || ""} color={isEquivalentMathExpression(answer.firstSelectedOption || answer.selectedOption, question.answer) ? colors.correctAnswerColor : colors.wrongAnswerColor} /></span>}
          <span className="text-[1.25rem]">Correct Answer: <MathField latex={question.answer} color={colors.correctAnswerColor} /></span>
          <div className="w-full flex flex-col justify-center items-center">
            <ExpandExplanation explanation={question?.explanation || ''} initialHideExplanation={answer && answer.firstIsCorrect || true} />
          </div>
        </div>
      </div>
    </div>
  )
}

const ReviewBlankFilling = ({ question, answer, questionNumber }: { question: PracticeQuestion, answer: AnswerRecord | null, questionNumber: number }) => {
  return (
    <div className="w-full">
      <div className="text-xl text-[#573F35] pb-4 flex items-start gap-3">
        <span style={{ ...titleStyle }}>{questionNumber}.</span>
        <FillingQuestion
          className="w-full text-black text-xl font-medium"
          question={question?.question}
          answer={question?.answer}
          userInput={Array.isArray(question?.answer) ? question.answer : [question.answer]}
          submit={false}
        />
      </div>

      <div className="flex flex-col w-full">
        <div className="w-full flex flex-col justify-center" style={{ color: colors.mainTextColor }}>
          {answer && <span className="text-[1.25rem]">Your Answer:
            <span className="text-[1.25rem] ml-2 font-bold" style={{ color: answer.firstIsCorrect ? colors.correctAnswerColor : colors.wrongAnswerColor }}>{answer.firstIsCorrect === undefined ? answer.selectedOption : answer.firstSelectedOption || ""} </span>
          </span>}
          <div className="w-full flex flex-col justify-center items-center">
            <ExpandExplanation explanation={question?.explanation || ''} initialHideExplanation={answer && answer.firstIsCorrect || true} />
          </div>
        </div>
      </div>
    </div>
  )
}

const ReviewMultipleChoice = ({ question, answer, questionNumber }: { question: PracticeQuestion, answer: AnswerRecord | null, questionNumber: number }) => {
  return (
    <div className="w-full text-[#573F35]">
      <div className="text-xl text-[#573F35] pb-4 flex gap-3 items-start">
        <span style={{ ...titleStyle }}>{questionNumber}.</span>
        <MarkdownComponent content={question?.question} textStyle={titleStyle} />
      </div>
      <div className="flex flex-col gap-3 items-center mt-6">
        {question?.options?.map((option: any, idx: number) => {
          let bgColor = "white";
          let borderColor = "#573F35";
          const isSelected = answer && (answer.firstSelectedOption ? answer.firstSelectedOption === option : answer.selectedOption === option) || false;
          const isCorrect = question.answer === option;

          if (isCorrect) {
            bgColor = colors.correctAnswerColorLight;
            borderColor = colors.correctAnswerColor;
          } else if (isSelected && !isCorrect) {
            bgColor = colors.wrongAnswerColorLight;
            borderColor = colors.wrongAnswerColor;
          }
          // 字母数组
          const optionLabels = ['A', 'B', 'C', 'D', 'E', 'F'];
          return (
            <div
              key={idx}
              className="w-[300px] sm:w-[400px] md:w-[600px] text-base font-bold rounded-xl px-4 py-3"
              style={{
                backgroundColor: bgColor,
                borderColor: borderColor,
                textAlign: 'left',
              }}
            >
              <span className="flex flex-row items-center text-[1.25rem]" style={{ color: colors.mainTextColor }}>
                <span className="mr-4 display-text">{optionLabels[idx]}.</span>
                <MarkdownComponent content={option} textStyle={{ textAlign: 'left', fontFamily: 'var(--font-display)', color: colors.mainTextColor, fontSize: '1.25rem', fontWeight: '400' }} />
              </span>
            </div>
          );
        })}
      </div>
      <div className="w-full flex flex-col items-center justify-center">
        <ExpandExplanation explanation={question?.explanation || ''} initialHideExplanation={answer && answer.firstIsCorrect || true} />
      </div>
    </div>
  )
}

// 垂直排列所有的question
const ReviewAnswer = ({ answerRecords, allQuestions, onClose, isSolution = false }: ReviewAnswerProps) => {
  return (
    <div className="flex flex-col gap-8 p-6 w-full max-w-[800px]">
      {allQuestions.map((question, index) => {
        // Find the corresponding answer record for this question
        const answerRecord = answerRecords.find(record => record.questionId === question.id);

        // If no answer record found, create a default one
        const defaultAnswer: AnswerRecord = {
          questionId: question.id,
          selectedOption: '',
          isCorrect: false,
          timestamp: 0
        };

        const answer = isSolution ? null : answerRecord || defaultAnswer;
        const questionNumber = index + 1;

        return (
          <div
            key={question.id}
            className="w-full border-b border-gray-300 pb-8"
          >
            {/* Render different components based on question type */}
            {question.type === QuestionType.MATH_BLANK_FILLING && (
              <ReviewMathFilling question={question} answer={answer} questionNumber={questionNumber} />
            )}
            {(question.type === QuestionType.BLANK_FILLING || question.type === QuestionType.GRAMMAR_FILL_IN_BLANK) && (
              <ReviewBlankFilling question={question} answer={answer} questionNumber={questionNumber} />
            )}
            {(question.type === QuestionType.MULTIPLE_CHOICE || question.type === QuestionType.GRAMMAR_CLASSIFICATION) && (
              <ReviewMultipleChoice question={question} answer={answer} questionNumber={questionNumber} />
            )}
            {question.type === QuestionType.DICTATION && (
              <ReviewDictation question={question} answer={answer} questionNumber={questionNumber} />
            )}
            {question.type === QuestionType.GRAMMAR_DRAG_AND_DROP && (
              <ReviewGrammarDragAndDrop question={question} answer={answer} questionNumber={questionNumber} />
            )}
            {question.type === QuestionType.HIGHLIGHT && (
              <ReviewHighlight question={question} answer={answer} questionNumber={questionNumber} />
            )}
          </div>
        );
      })}
      <NormalButton onClick={onClose}>
        Return
      </NormalButton>
    </div>
  )
}

export default ReviewAnswer;